import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FixedDataPicker } from '../fixed-data-picker/FixedDataPicker';
import { IPickerProps } from '../types';
import { Selection } from '../utils/selection-model';

export type EnumValue = string | number;
export type EnumEntityLike = {
  id: EnumValue;
  name: string;
};

export type EnumEntityLikePickerProps<TMultiple extends boolean> = Omit<
  IPickerProps<{ id: string; name: string }, TMultiple>,
  'onChange' | 'value'
> & {
  value: Selection<EnumEntityLike, TMultiple>;
  onChange: (input: EnumEntityLike | EnumEntityLike[]) => void;
  // Definitions
  values: EnumValue[];
  translationPrefix?: string;
  params?: {
    values: EnumValue[];
    translationPrefix?: string;
  };
};

export function EnumEntityLikePicker<TMultiple extends boolean>(
  props: EnumEntityLikePickerProps<TMultiple>
) {
  const {
    values: inputValues,
    value,
    onChange,
    multiple,
    translationPrefix: inputTranslationPrefix,
  } = props;
  const values = inputValues ?? props.params?.values;
  const translationPrefix =
    inputTranslationPrefix ?? props.params?.translationPrefix;

  const { t } = useTranslation();

  const dataSet = useMemo<EnumEntityLike[]>(() => {
    const prefix = translationPrefix || 'ENUMS';
    return values
      .map((x) => {
        const name = x.toString().toUpperCase();
        return { id: x, name: t(`${prefix}.${name}`) };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [translationPrefix, values, t]);

  const handleChange = useCallback(
    (newValue: EnumEntityLike | EnumEntityLike[]): void => {
      onChange(newValue);
    },
    [onChange]
  );

  return (
    <FixedDataPicker
      value={value}
      multiple={multiple}
      onChange={handleChange}
      dataSet={dataSet}
    />
  );
}
